/**
 * Deterministic formatting.
 *
 * Locale and time zone are fixed rather than taken from the browser so that a
 * promotion date reads identically for every clinician reviewing the same
 * project, and so visual-regression baselines do not depend on the machine that
 * captured them.
 */
const DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

const DATE_TIME_FORMAT = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "UTC",
  timeZoneName: "short",
});

export const EM_DASH = "—";

export function formatDate(value: string | null | undefined): string {
  if (!value) {
    return EM_DASH;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? EM_DASH : DATE_FORMAT.format(parsed);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return EM_DASH;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? EM_DASH
    : DATE_TIME_FORMAT.format(parsed);
}

/**
 * Formats an epoch-millisecond reading — TanStack Query's `dataUpdatedAt`, which
 * is a number rather than one of the contract's ISO strings.
 *
 * `0` means the query has never resolved, so it reads as the em dash rather than
 * as the epoch: a stale notice claiming data from 1970 is worse than one that
 * admits it does not know.
 */
export function formatTimestamp(value: number): string {
  if (!value) {
    return EM_DASH;
  }
  return DATE_TIME_FORMAT.format(new Date(value));
}

// #153 removed `formatVersionPair`: the generic `Configuration vN · Engine vN`
// presentation implied an environment-owned version pair. The identities shown
// instead are real and independent — pack digests, candidate hashes, pins.

/** Enough of an immutable digest to quote to a release owner, without the wall of hex. */
export function shortHash(value: string): string {
  return value.slice(0, 12);
}

const BYTE_UNITS = ["bytes", "KB", "MB", "GB"] as const;

/**
 * A byte count a clinician can read, from a `byteLength` the contract states
 * exactly.
 *
 * Decimal units rather than binary, and rounded above the first step, because
 * the value is context beside a file's content rather than a figure anybody
 * reconciles against — the exact identity of those bytes is the `sha256`
 * rendered beside it, and that one is never abbreviated. `0` reads as
 * `0 bytes`, not as the em dash: an empty frozen file is a real thing an
 * archive can hold, and it is not a missing reading.
 *
 * Locale-independent by construction, for the reason stated at the top of this
 * file: two clinicians reading one project must read the same string.
 */
export function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value < 0) {
    return EM_DASH;
  }
  let size = value;
  let unit = 0;
  while (size >= 1000 && unit < BYTE_UNITS.length - 1) {
    size /= 1000;
    unit += 1;
  }
  // `?? BYTE_UNITS[0]` satisfies `noUncheckedIndexedAccess`; the loop above
  // already bounds `unit` to the array, so the fallback is unreachable.
  const label = BYTE_UNITS[unit] ?? BYTE_UNITS[0];
  return `${unit === 0 ? size : Math.round(size * 10) / 10} ${label}`;
}
